import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Compass, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * AI Navigation Summary — the trend-interpretation layer above the
 * graph. No new math: it narrates the journey-stage aggregates the
 * ribbon already shows. On-demand generation with server-side caching
 * keyed on the stage snapshot, so repeat visits are instant.
 */
export function NavigationSummaryCard({ orgId, stageHealth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const ready = !!stageHealth?.navigation;

  const payload = useCallback(() => ({
    ...(orgId && orgId !== 'all' ? { organization_id: orgId } : {}),
    stage_health: Object.fromEntries(
      Object.entries(stageHealth || {})
        .filter(([, v]) => v)
        .map(([k, v]) => [k, {
          latest: Math.round(v.latest * 10) / 10,
          change_pct: v.changePct == null ? null : Math.round(v.changePct * 10) / 10,
          signal_count: v.signalCount,
        }]),
    ),
  }), [orgId, stageHealth]);

  const fetchSummary = useCallback(async ({ force = false, cachedOnly = false } = {}) => {
    if (!ready) return;
    if (!cachedOnly) setLoading(true);
    try {
      const res = await api.post('/navigation-signals/summary', {
        ...payload(),
        force,
        cached_only: cachedOnly,
      });
      setData(res.data?.available === false ? null : res.data);
    } catch (err) {
      if (!cachedOnly) toast.error(err.response?.data?.detail || 'Failed to generate navigation summary');
    }
    setLoading(false);
  }, [ready, payload]);

  useEffect(() => {
    setData(null);
    fetchSummary({ cachedOnly: true });
  }, [fetchSummary]);

  if (!ready) return null;

  return (
    <div className="rounded-xl border border-cyan-200 bg-cyan-50/50 p-4" data-testid="navigation-summary-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] font-bold uppercase tracking-widest text-cyan-800 flex items-center gap-1.5">
          <Compass className="h-3.5 w-3.5" /> AI Navigation Summary
        </p>
        {data ? (
          <Button
            size="sm"
            variant="ghost"
            className="gap-1 text-[11px] text-cyan-700 hover:bg-cyan-100 h-7 px-2"
            onClick={() => fetchSummary({ force: true })}
            disabled={loading}
            data-testid="nav-summary-regenerate-btn"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        ) : (
          <Button
            size="sm"
            className="gap-1.5 bg-cyan-700 hover:bg-cyan-800 text-white h-7"
            onClick={() => fetchSummary()}
            disabled={loading}
            data-testid="nav-summary-generate-btn"
          >
            {loading
              ? <><RefreshCw className="h-3 w-3 animate-spin" /> Interpreting…</>
              : <><Sparkles className="h-3.5 w-3.5" /> Generate</>}
          </Button>
        )}
      </div>
      {loading && !data ? (
        <p className="text-sm text-slate-500 mt-2">Interpreting how your ability to navigate toward value is changing…</p>
      ) : data ? (
        <>
          <p className="text-sm text-slate-700 leading-relaxed mt-2" data-testid="nav-summary-text">{data.summary}</p>
          <p className="text-[10px] text-slate-400 mt-1.5">
            {data.cached ? 'Cached · ' : ''}{data.generated_at ? new Date(data.generated_at).toLocaleString() : ''} · interpretation only — no new math
          </p>
        </>
      ) : (
        <p className="text-sm text-slate-500 mt-2">
          One-paragraph executive interpretation of the journey-stage trends above — which stages are improving, and which are constraining navigation.
        </p>
      )}
    </div>
  );
}
